/**
 * Mock `createGi` — turns a `SparkDraft` into a fully-formed,
 * cryptographically valid `GiBundle` and persists it into the store.
 *
 * The resulting `.gi` bundle is byte-identical (modulo JCS canon caveat
 * documented in `sign.ts`) to what the real Rust backend would emit.
 * A user who Sparks in the mock and later verifies in the real engine
 * gets a clean pass.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { bytesToHex } from '@noble/hashes/utils';

import type {
  ActivityEvent,
  Ed25519Signature,
  EntityId,
  GiBundle,
  GiStatus,
  GiSummary,
  Hash256,
  Lineage,
  LineageEntry,
  Manifest,
  SignedEntity,
  SparkDraft,
  SparkOutput,
  TrustedTimestamp,
} from '../types';

import {
  appendActivity,
  nowTimestamp,
  upsertRecord,
  type GiRecord,
} from './store';
import {
  canonicalHash,
  canonicalJsonStringify,
  generateKeyPair,
  hashBytes,
  randomHex,
  signDigest,
} from './sign';

const MOCK_LATENCY_MS = 200;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function createGi(
  draft: SparkDraft,
  wasm_bytes?: Uint8Array,
): Promise<SparkOutput> {
  await sleep(MOCK_LATENCY_MS);

  // Every new GI generates a fresh author keypair — in the real
  // engine the author keypair lives in the Vault and is reused; here
  // we generate per-GI since the UI doesn't yet own a persistent
  // identity. A Phase 4 identity layer consolidates this.
  const kp = generateKeyPair();

  const root_id = randomUuid();
  const created_at: TrustedTimestamp = nowTimestamp();

  const wasm_hash: Hash256 =
    draft.wasm_hash ?? (wasm_bytes ? hashBytes(wasm_bytes) : `sha256:${randomHex(32)}`);
  const source_hash: Hash256 = draft.source_hash ?? `sha256:${randomHex(32)}`;

  // Contract is the exact user-declared shape, no conductor_dag in the
  // Spark flow (conductors will have their own flow later).
  const contract = {
    inputs: draft.inputs,
    outputs: draft.outputs,
    parameters: draft.parameters,
    behavior_rules: [],
    domain_adapter: draft.domain,
    macros: [],
  };
  const contract_hash = canonicalHash(contract);

  const manifest: Manifest = {
    gi_version: '0.1.0',
    root_id,
    version: draft.version,
    name: draft.name,
    description: draft.description,
    domain: draft.domain,
    author: { name: draft.author_name, public_key: kp.public_key },
    contract_hash,
    source_hash,
    wasm_hash,
    compiler: { name: 'mock-compiler', version: '0.1.0', target: 'wasm32-unknown' },
    lineage: { root_id, depth: 0 },
    created_at,
  };

  const manifest_hash = canonicalHash(manifest);
  const signature: Ed25519Signature = signDigest(
    hexToBytes(manifest_hash.slice('sha256:'.length)),
    kp,
  );

  const signed_entity: SignedEntity = {
    root_id,
    version: manifest.version,
    manifest_hash,
    signature,
    signer_public_key: kp.public_key,
    created_at,
  };

  const lineage: Lineage = {
    root_id,
    entries: [
      {
        index: 0,
        root_id,
        version: manifest.version,
        action: 'Genesis',
        parent_hash: null,
        entry_hash: canonicalHash({ root_id, version: manifest.version, parent: null }),
        signer: kp.public_key,
        signed_at: created_at,
      } satisfies LineageEntry,
    ],
  };

  const bundle: GiBundle = {
    bundle: 'gi/v1',
    manifest,
    signed_entity,
    contract,
    lineage,
  };
  const bundle_bytes = new TextEncoder().encode(canonicalJsonStringify(bundle));

  const record: GiRecord = {
    manifest,
    signed_entity,
    contract,
    lineage,
    attestations: [],
    statuses: ['signed'] satisfies GiStatus[] as GiStatus[],
    counters: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
    secret_key_hex: bytesToHex(kp.secret_key),
  };
  upsertRecord(record);

  const ev: ActivityEvent = {
    kind: 'sparked',
    root_id,
    name: manifest.name,
    at: created_at,
  };
  appendActivity(ev);

  const summary: GiSummary = {
    root_id,
    version: manifest.version,
    name: manifest.name,
    description: manifest.description,
    domain: manifest.domain,
    author_name: manifest.author.name,
    created_at,
    status: record.statuses,
    stats: record.counters,
  };

  return { bundle, summary, bundle_bytes };
}

// ─── Helpers ───────────────────────────────────────────────────────

function randomUuid(): EntityId {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  const hex = randomHex(16);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    `4${hex.slice(13, 16)}`,
    `8${hex.slice(17, 20)}`,
    hex.slice(20, 32),
  ].join('-');
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

// Re-export Ed25519 verify so the verify handler uses the same curve
// backend as spark. Prevents accidental drift.
export const verifyEd25519 = ed25519.verify.bind(ed25519);
